import React, {
  Fragment,
  memo,
  PropsWithChildren,
  ReactNode,
  useCallback,
  useMemo,
} from 'react';
import { IconChevronLeft } from '@app/icons';
import {
  dateFormat,
  formatEndDate,
  moneyFormat,
  hidePhoneNumbers,
  generateReadableNumber,
} from '@app/helpers';
import { useNavigate } from 'react-router-dom';
import {
  MeasureLabelsShort,
  Tender,
  TenderCategory,
  TenderCategoryNames,
  TenderTypeNames,
  TenderTypes,
} from '@app/models';
import placeholderImage from 'pages/Tender/placeholder.png';
import {
  Collapse,
  Empty,
  FilePicker,
  Info,
  LoadableImage,
  Tabs,
} from '@app/components';
import { Replies } from './components';
import { useUserContext } from '@app/providers';
import { Button } from 'components/ui/button';
import { Card, CardContent, CardHeader } from 'components/ui/card';
import { Badge } from 'components/ui/badge';
import {
  Tabs as ShadcnTabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from 'components/ui/tabs';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from 'components/ui/collapsible';
import { ChevronDown } from 'lucide-react';

interface Props {
  tender: Tender;
  actions?: ReactNode[];
  viewReplies?: boolean;
  editable?: boolean;
}

function TenderInfo(props: Props & PropsWithChildren) {
  const { tender, actions = [], viewReplies = false, editable = false } = props;
  const { user } = useUserContext();
  const navigate = useNavigate();

  const onClickBack = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();

      navigate(-1);
    },
    [navigate]
  );

  const tenderImages = useMemo(() => {
    if (!tender.imagesIds) {
      return [];
    }

    return tender.imagesIds.split(',');
  }, [tender.imagesIds]);

  const tabLabels = useMemo(() => {
    const returnTabLabels = ['Общая информация'];
    if (user.authenticated) {
      returnTabLabels.push('Документы и Файлы');
    }
    if (viewReplies) {
      returnTabLabels.push('Отклики');
    }

    return returnTabLabels;
  }, [user.authenticated, viewReplies]);

  const price = () => {
    if (!tender.price) return ' ';

    let addText = '';

    if (tender.type === TenderTypes.REQUEST && tender.measure) {
      addText = `/${MeasureLabelsShort[tender.measure]}`;
    }

    return `${moneyFormat(tender.price)}${addText}`;
  };

  const tabContent = useMemo<ReactNode[]>(() => {
    if (!tender) {
      return [];
    }

    const content = [
      <div className="space-y-4">
        <Info label="Регион" value={tender.kato.nameRu} />
        <Info label="Категория" value={tender.oked?.nameRu} />
        <Info
          label="Описание"
          value={
            user.authenticated
              ? tender.description
              : hidePhoneNumbers(tender.description)
          }
        />
        <Info
          label="Требования"
          value={
            <div className="flex flex-wrap gap-2">
              {tender.requirements.map(
                (requirement, requirementIndex) =>
                  requirement.name && (
                    <div
                      key={requirementIndex.toString()}
                      className="bg-gray-100 rounded-lg px-3 py-2 text-sm font-normal text-black break-words"
                    >
                      {requirement.name}
                    </div>
                  )
              )}
            </div>
          }
        />
        <div className="flex flex-col sm:flex-row gap-3 w-full">
          {actions.map((action, actionIndex) => (
            <Fragment key={actionIndex}>{action}</Fragment>
          ))}
        </div>
      </div>,
    ];

    if (user.authenticated) {
      const filesArray = tender.filesIds ? tender.filesIds.split(',') : [];
      const hasFiles = filesArray.length > 0;
      content.push(
        hasFiles ? (
          <FilePicker
            loadableFiles={filesArray}
            onChange={() => ''}
            editable={editable}
          />
        ) : (
          <Empty title="Нет доступных документов" />
        )
      );
    }
    if (viewReplies) {
      const repliesArray = tender.replies || [];
      content.push(
        repliesArray.length > 0 ? (
          <Replies replies={repliesArray} />
        ) : (
          <Empty title="Нет откликов" />
        )
      );
    }

    return [...content];
  }, [actions, editable, tender, user.authenticated, viewReplies]);

  return (
    <div className="w-full max-w-7xl mx-auto px-2 sm:px-4 lg:px-8">
      <div className="grid gap-4 overflow-hidden">
        <Card className="bg-white rounded-lg shadow-sm overflow-hidden">
          <CardHeader className="p-2 sm:p-4">
            <div className="flex items-center gap-2 sm:gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={onClickBack}
                className="w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 bg-gray-200 rounded-full flex items-center justify-center border-none outline-none text-blue-600 cursor-pointer hover:bg-gray-100 flex-shrink-0"
              >
                <IconChevronLeft className="h-3 w-3 sm:h-4 sm:w-4 lg:h-5 lg:w-5" />
              </Button>

              <div className="flex-1 min-w-0">
                <h1 className="font-medium text-sm sm:text-lg lg:text-xl xl:text-2xl leading-tight text-gray-900 truncate">
                  {tender.name}
                </h1>

                <div className="flex flex-col sm:flex-row gap-1 sm:gap-4 mt-1 sm:mt-2">
                  <div className="flex items-center text-gray-500 text-xs sm:text-sm">
                    <span>Тип:</span>
                    <span className="ml-1 text-gray-700">
                      {TenderTypeNames[tender.type]}
                    </span>
                  </div>
                  <div className="flex items-center text-gray-500 text-xs sm:text-sm">
                    <span>Категория:</span>
                    <span className="ml-1 text-gray-700">
                      {TenderCategoryNames[tender.category]}
                    </span>
                  </div>
                </div>
              </div>

              {!!tender.endDate && new Date(tender.endDate) > new Date() && (
                <Badge className="bg-green-600 hover:bg-green-700 rounded text-white px-2 py-1 text-xs sm:text-sm flex-shrink-0">
                  {formatEndDate(tender.endDate)}
                </Badge>
              )}
            </div>
          </CardHeader>
        </Card>

        <Card className="bg-white rounded-lg shadow-sm overflow-hidden">
          <CardContent className="p-2 sm:p-4 lg:p-6">
            <div className="flex flex-col lg:flex-row gap-3 sm:gap-4 lg:gap-8 overflow-hidden">
              <div className="w-full lg:w-80 xl:w-96 flex-shrink-0">
                <div className="w-full h-40 sm:h-48 lg:h-64 xl:h-80 overflow-hidden rounded-md">
                  <LoadableImage
                    imageIds={tenderImages}
                    placeholder={placeholderImage}
                  />
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <div className="grid gap-2 sm:gap-3 lg:gap-4 h-full">
                  <Info
                    label="Номер объявления"
                    value={'№ ' + generateReadableNumber(tender.id)}
                  />
                  <Info
                    label="Дата публикации"
                    value={dateFormat(tender.createdAt)}
                  />
                  <Info
                    label={
                      tender.category === TenderCategory.WORKER
                        ? 'Дата начала работ'
                        : 'Срок пыоставки'
                    }
                    value={dateFormat(tender.deliveryDate)}
                  />
                  <Info
                    label="Объем"
                    value={
                      <p className="font-medium text-base sm:text-lg lg:text-xl text-blue-600 truncate">
                        {price()}
                      </p>
                    }
                  />
                </div>
              </div>
            </div>
            <div className="mt-4 sm:mt-6 hidden sm:block">
              <ShadcnTabs defaultValue="0" className="w-full">
                <TabsList
                  className="grid w-full"
                  style={{
                    gridTemplateColumns: `repeat(${tabLabels.length}, 1fr)`,
                  }}
                >
                  {tabLabels.map((label, index) => (
                    <TabsTrigger
                      key={index}
                      value={index.toString()}
                      className="text-xs sm:text-sm"
                    >
                      {label}
                    </TabsTrigger>
                  ))}
                </TabsList>
                {tabContent.map((content, index) => (
                  <TabsContent
                    key={index}
                    value={index.toString()}
                    className="mt-4"
                  >
                    {content}
                  </TabsContent>
                ))}
              </ShadcnTabs>
            </div>
            <div className="mt-4 sm:mt-6 block sm:hidden">
              {tabLabels.map((label, index) => (
                <Collapsible
                  key={label}
                  className="mb-3"
                  defaultOpen={index === 0}
                >
                  <CollapsibleTrigger className="flex w-full items-center justify-between rounded-lg border px-3 py-2 font-medium text-sm [&[data-state=open]>svg]:rotate-180">
                    {label}
                    <ChevronDown className="h-4 w-4 shrink-0 transition-transform duration-200" />
                  </CollapsibleTrigger>
                  <CollapsibleContent className="px-3 py-3 border-l border-r border-b rounded-b-lg">
                    {tabContent[index]}
                  </CollapsibleContent>
                </Collapsible>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default memo(TenderInfo);
